import {
  PayoutDestinationType,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import { IAccount } from "@wingspanhq/users/dist/lib/interfaces";
import {
  WSActions,
  WSCard,
  WSControl,
  WSControlGroupCards,
  WSEmptyState,
  WSInfoBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { WSQueryCache } from "@ws-react-query";
import { useCallback, useState } from "react";
import { useQueryInternalAccounts } from "../../../../query/banking/queries/useQueryInternalAccounts";
import { useWSMutation } from "../../../../query/helpers";
import { useUserId } from "../../../../query/hooks/helpers";
import { QUERY_PAYOUT_SETTINGS } from "../../../../query/payments/keys";
import { useQueryPayoutSettings } from "../../../../query/payments/queries";
import { paymentsService } from "../../../../services/payments";
import {
  avatarBankAccount,
  avatarBankCard,
  avatarWallet
} from "../../../../shared/constants/avatars";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { selectorInstantPayoutDefaultFee } from "../../../../shared/selectors/selectorInstantPayoutDefaultFee";
import { selectorPendingBankingAccount } from "../../../../shared/selectors/selectorPendingBankingAccount";
import { WSServiceError } from "../../../../utils/serviceHelper";

type UnavailableSelection = "debit-card" | "try-another-bank" | "wallet";

type Props = {
  failedAccount?: IAccount;
  onBack: () => void;
  onNext?: () => void;
  onDebitCard: () => void;
  onTryAnotherBank: () => void;
  onActivateWallet: () => void;
};

export const InstantUnavailable: React.FC<Props> = props => {
  const userId = useUserId();
  const queryPayoutSettings = useQueryPayoutSettings(userId);
  const queryInternalAccounts = useQueryInternalAccounts();

  const hasActiveWallet = (queryInternalAccounts.data || []).some(
    selectorActiveBankingAccount
  );
  const hasPendingWallet = (queryInternalAccounts.data || []).some(
    selectorPendingBankingAccount
  );
  const fee = selectorInstantPayoutDefaultFee(queryPayoutSettings.data);

  const [selectedOption, setSelectedOption] = useState<
    UnavailableSelection | undefined
  >(undefined);
  const [useAsStandard, setUseAsStandard] = useState(false);

  const [setStandardPayout, setStandardPayoutMeta] = useWSMutation<
    void,
    WSServiceError
  >(
    async () => {
      if (!props.failedAccount) return;

      const payoutSettings = await paymentsService.payoutSettings.update(
        userId,
        {
          payoutPreferences: PayoutPreferences.Standard,
          standard: {
            destinationId: props.failedAccount.accountId,
            destinationType: PayoutDestinationType.Account
          }
        }
      );

      WSQueryCache.setQueryData(QUERY_PAYOUT_SETTINGS, payoutSettings);
    },
    {
      onSuccess: props.onNext,
      dependencies: [QUERY_PAYOUT_SETTINGS]
    }
  );

  const [setWalletAsInstantPayout, setWalletMeta] = useWSMutation<
    void,
    WSServiceError
  >(
    async () => {
      const activeWallet = (queryInternalAccounts.data || []).find(
        selectorActiveBankingAccount
      );
      if (!activeWallet) return;

      const payoutSettings = await paymentsService.payoutSettings.update(
        userId,
        {
          payoutPreferences: PayoutPreferences.Instant,
          instant: {
            destinationId: activeWallet.internalAccountId,
            destinationType: "InternalAccount" as PayoutDestinationType
          }
        }
      );

      WSQueryCache.setQueryData(QUERY_PAYOUT_SETTINGS, payoutSettings);
    },
    {
      onSuccess: props.onNext,
      dependencies: [QUERY_PAYOUT_SETTINGS]
    }
  );

  const handleContinue = useCallback(() => {
    if (useAsStandard) {
      setStandardPayout();
      return;
    }

    if (selectedOption === "debit-card") {
      props.onDebitCard();
    } else if (selectedOption === "try-another-bank") {
      props.onTryAnotherBank();
    } else if (selectedOption === "wallet") {
      if (hasActiveWallet) {
        setWalletAsInstantPayout();
      } else {
        props.onActivateWallet();
      }
    }
  }, [
    useAsStandard,
    selectedOption,
    hasActiveWallet,
    setStandardPayout,
    setWalletAsInstantPayout,
    props
  ]);

  const isSelectionMade = useAsStandard || selectedOption !== undefined;
  const isLoading = setStandardPayoutMeta.isLoading || setWalletMeta.isLoading;

  return (
    <WSList gap="2XL">
      <WSEmptyState
        type="search"
        title="Instant payout unavailable"
        description="This account isn't eligible for instant payouts. Please select a different account or choose a standard payout method."
        bgColor="white"
        orientation="horizontal"
        hasBorder
      />

      <WSCard
        header={{
          label: {
            text: "Try another method"
          }
        }}
      >
        <WSList gap="2XL">
          <WSText.ParagraphSm color="gray500">
            Connect a debit card, use your Wingspan Wallet, try another bank, or
            switch to standard payout.
          </WSText.ParagraphSm>

          <WSControlGroupCards
            value={selectedOption ?? ""}
            onChange={newValue => {
              setSelectedOption(newValue as UnavailableSelection);
              setUseAsStandard(false);
            }}
            options={[
              {
                header: {
                  label: {
                    text: `Connect a debit card (${fee}% fee)`,
                    helperText:
                      "Receive payments by connecting an eligible Visa or Mastercard debit card",
                    avatar: avatarBankCard
                  }
                },
                value: "debit-card"
              },
              {
                header: {
                  label: {
                    text: hasActiveWallet
                      ? `Wingspan Wallet (${fee}% fee)`
                      : `Activate Wingspan Wallet (${fee}% fee)`,
                    helperText: hasActiveWallet
                      ? "Use your Wingspan Wallet as your instant payout method"
                      : "Business bank account with instant access to your funds",
                    avatar: avatarWallet
                  },
                  ...(hasPendingWallet
                    ? {
                        value: {
                          pill: {
                            theme: "blue" as const,
                            text: "Pending activation",
                            badge: true,
                            type: "noBackground" as const
                          },
                          stacked: true
                        }
                      }
                    : {})
                },
                disabled: hasPendingWallet,
                value: "wallet"
              },
              {
                header: {
                  label: {
                    text: "Try another bank",
                    helperText:
                      "Try instant payout by connecting an account from another bank",
                    avatar: avatarBankAccount
                  }
                },
                value: "try-another-bank"
              }
            ]}
          />

          <WSInfoBox theme="info">
            <WSText.ParagraphXs mb="L" color="gray500">
              If you wish to use the bank account you provided as a{" "}
              <b>standard payout method</b> instead, check the box below.
            </WSText.ParagraphXs>

            <WSControl
              type="checkbox"
              value={useAsStandard}
              onChange={checked => {
                setUseAsStandard(checked);
                if (checked) {
                  setSelectedOption(undefined);
                }
              }}
              label="Add this bank account and receive you funds in the standard 2-5 business days."
              size="S"
            />
          </WSInfoBox>
        </WSList>
      </WSCard>

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Back",
            kind: "Tertiary",
            onClick: props.onBack
          },
          {
            label: "Continue",
            disabled: !isSelectionMade,
            loading: isLoading,
            onClick: handleContinue
          }
        ]}
      />
    </WSList>
  );
};
